import { useMemo } from "react";
import { usePartnerRole } from "@/hooks/usePartnerRole";

// Available partner modules
export const PARTNER_MODULES = [
  { key: 'partner_dashboard', label: 'Dashboard', description: 'Painel principal com métricas' },
  { key: 'partner_financials', label: 'Receita & Clientes', description: 'Dados financeiros e faturamento' },
  { key: 'partner_reports', label: 'Relatórios Ads', description: 'Métricas de anúncios' },
  { key: 'partner_orgs', label: 'Organizações', description: 'Gestão de clientes' },
  { key: 'partner_courses', label: 'Cursos', description: 'Trilhas de aprendizado' },
  { key: 'partner_team_payments', label: 'Pagamento de Equipe', description: 'Gestão de pagamentos da equipe' },
] as const;

export type PartnerModuleKey = typeof PARTNER_MODULES[number]['key'];

export interface PartnerPermissions {
  // Permissões por módulo (visualização)
  canViewDashboard: boolean;
  canViewOrgs: boolean;
  canViewUsers: boolean;
  canViewTeam: boolean;
  canViewCourses: boolean;
  canViewSettings: boolean;
  canViewReports: boolean;
  canViewReceitaClientes: boolean;
  canViewTeamPayments: boolean;

  // Dashboard - visualização de dados financeiros
  canViewFinancials: boolean;

  // Permissões de ação em Organizações
  canCreateOrgs: boolean;
  canEditOrgs: boolean;
  canDeleteOrgs: boolean;
  canToggleOrgActive: boolean;

  // Permissões de ação em Usuários
  canCreateUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;
  canResetUserPassword: boolean;

  // Permissões na Equipe
  canManageMembers: boolean;
  canEditMemberRole: boolean;

  // Permissões em Settings
  canEditSettings: boolean;
  canEditBranding: boolean;
}

export function usePartnerPermissions(): PartnerPermissions {
  const { memberInfo } = usePartnerRole();

  const permissions = useMemo<PartnerPermissions>(() => {
    const role = memberInfo?.role;
    const allowedModules = memberInfo?.allowed_modules;
    const isOwner = role === "owner";
    const isAdmin = role === "admin";
    const isSupport = role === "support";
    const isFinanceiro = role === "financeiro";

    // Helper to check if module is allowed
    // NULL = all modules based on role (backward compatible)
    // Array = only listed modules are allowed
    const hasModule = (moduleKey: string): boolean => {
      if (isOwner) return true; // Owner always has full access
      if (!allowedModules) return true; // NULL = use role-based defaults
      return allowedModules.includes(moduleKey);
    };

    return {
      // Permissões por módulo (visualização) - now consider allowed_modules
      canViewDashboard: hasModule('partner_dashboard'),
      canViewOrgs: hasModule('partner_orgs') || isFinanceiro, // Financeiro can view orgs
      canViewUsers: hasModule('partner_orgs') || isFinanceiro, // Users are accessed via orgs
      canViewTeam: isOwner, // Only owner can view team management
      canViewCourses: hasModule('partner_courses'),
      canViewSettings: isOwner, // Only owner can view settings
      canViewReports: hasModule('partner_reports'),
      canViewReceitaClientes: hasModule('partner_financials') && (isOwner || isAdmin || isFinanceiro),
      canViewTeamPayments: hasModule('partner_team_payments') && (isOwner || isFinanceiro),

      // Dashboard - visualização de dados financeiros
      canViewFinancials: hasModule('partner_financials') && (isOwner || isAdmin || isFinanceiro),

      // Permissões de ação em Organizações
      canCreateOrgs: (isOwner || isAdmin) && hasModule('partner_orgs'),
      canEditOrgs: hasModule('partner_orgs'), // todos com acesso ao módulo podem editar dados básicos
      canDeleteOrgs: isOwner, // apenas owner deleta
      canToggleOrgActive: (isOwner || isAdmin) && hasModule('partner_orgs'),

      // Permissões de ação em Usuários
      canCreateUsers: (isOwner || isAdmin) && hasModule('partner_orgs'),
      canEditUsers: hasModule('partner_orgs'), // todos com acesso podem editar dados
      canDeleteUsers: isOwner, // apenas owner remove
      canResetUserPassword: hasModule('partner_orgs'), // todos com acesso podem resetar senha

      // Permissões na Equipe
      canManageMembers: isOwner, // apenas owner
      canEditMemberRole: isOwner, // apenas owner

      // Permissões em Settings
      canEditSettings: isOwner, // apenas owner
      canEditBranding: isOwner, // apenas owner
    };
  }, [memberInfo?.role, memberInfo?.allowed_modules]);

  return permissions;
}

// Helper type for permission keys
export type PartnerPermissionKey = keyof PartnerPermissions;
